/* eslint-disable max-lines */
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { take } from 'rxjs/operators';

import { filterBoolean, isExecutableFile } from '@recruitee/common';
import {
  Attachment,
  MailboxMessageAttachmentAssignment,
  VisibilityLevel,
} from '@recruitee/http-types';
import { I18nService, TranslatePipe } from '@recruitee/i18n';

import { AvatarComponent } from '../../../common/avatar/avatar.component';
import { BadgeComponent } from '../../../common/badge/badge.component';
import { ButtonComponent } from '../../../common/button/components/button/button.component';
import { IconButtonComponent } from '../../../common/button/components/icon-button/icon-button.component';
import { DateComponent } from '../../../common/date/rt-date/date.component';
import { DividerComponent } from '../../../common/divider/divider.component';
import { DropdownItemComponent } from '../../../common/dropdown/dropdown-item/dropdown-item.component';
import { DropdownComponent } from '../../../common/dropdown/dropdown.component';
import { IconColorDirective } from '../../../common/icon/directives/icon-color.directive';
import { IconComponent } from '../../../common/icon/icon.component';
import { FileSizePipe } from '../../../common/pipes/file-size.pipe';
import { SpinnerComponent } from '../../../common/spinner/spinner.component';
import { RtTooltip } from '../../../common/tooltip/tooltip.directive';
import { TruncateComponent } from '../../../common/truncate/truncate.component';
import { WindowRef } from '../../../common/window-ref';
import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../../../core/overlay/overlay-directives';
import { DialogWidth } from '../../../dialog/dialog-config';
import { DialogService } from '../../../dialogs/dialogs.service';
import { IconName } from '../../../icons-container/icons.type';
import { AcceptedFileTypesService } from '../../../utils/directives/accepted-file-types/accepted-file-types.service';
import { FileTypesCategoryEnum } from '../../../utils/directives/accepted-file-types/accepted-file-types.types';
import {
  AttachmentPreviewDialogComponent,
  AttachmentPreviewDialogData,
} from '../attachment-preview-dialog/attachment-preview-dialog.component';
import { AttachmentType } from './attachment-item.types';

interface AttachmentAction {
  label: string;
  icon: IconName;
  variant?: string;
  handler: () => void;
}

enum AttachmentActionId {
  SET_AS_CV,
  SET_AS_COVER_LETTER,
  DELETE,
}

export interface AttachmentItemTranslations {
  setAsCv: string;
  setAsCoverLetter: string;
  delete: string;
  previewDialog: AttachmentPreviewDialogData['translations'];
  executableWarningDialog: {
    title: string;
    message: string;
    agreeLabel: string;
    cancelLabel: string;
  };
}

type AttachmentAvatarData = {
  photoThumbUrl: string;
  hasAvatar: boolean;
  context: 'candidate' | 'other' | 'admin';
  initials: string;
  type: 'icon' | 'image' | 'initials';
  iconName?: IconName;
};

@Component({
  selector: 'rt-attachment-item',
  templateUrl: './attachment-item.component.html',
  styleUrls: ['./attachment-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.attachment-list]': 'type === "list-item"',
    '[class.preview-large]': 'type === "preview-large"',
    '[class.overlay-hidden]':
      '(!!emptyStateTemplate && !attachment.imageThumbnailUrl) || imageLoading',
  },
  standalone: true,
  imports: [
    AvatarComponent,
    BadgeComponent,
    ButtonComponent,
    ConnectedOverlayDirective,
    DateComponent,
    DividerComponent,
    DropdownComponent,
    DropdownItemComponent,
    FileSizePipe,
    IconButtonComponent,
    IconColorDirective,
    IconComponent,
    NgClass,
    NgTemplateOutlet,
    OverlayOriginDirective,
    RtTooltip,
    TranslatePipe,
    TruncateComponent,
    SpinnerComponent,
  ],
})
export class AttachmentItemComponent {
  @Input() public type: AttachmentType = 'card';
  @Input() public attachment: Attachment;
  @Input() public attachmentAssignment: MailboxMessageAttachmentAssignment;
  @Input() public hasSetAsCvAction: boolean = false;

  @Input() public hasSetAsCoverLetterAction: boolean = false;
  @Input() public hasDeleteAction: boolean = false;
  @Input() public hasDownloadAction: boolean = true;
  @Input() public highlightedFilename: string | null = null;
  @Input() public imageLoading: boolean;

  @Input() public moreTemplate: TemplateRef<any> | null;
  @Input() public footerIcon?: IconName;
  @Input() public translations: AttachmentItemTranslations = {
    setAsCv: 'rtu.attachment.set_as_cv',
    setAsCoverLetter: 'rtu.attachment.set_as_cover_letter',
    delete: 'app.common.delete',
    previewDialog: {
      closeDialogTooltip: 'app.ui.dialog.close_tooltip',
      downloadFile: 'app.attachment.download_file',
    },
    executableWarningDialog: {
      title: 'rtu.attachment.executable_warning_dialog.title',
      message: 'rtu.attachment.executable_warning_dialog.message',
      agreeLabel: 'rtu.attachment.executable_warning_dialog.agree_label',
      cancelLabel: 'rtu.attachment.executable_warning_dialog.cancel_label',
    },
  };
  @Input() public emptyStateTemplate: TemplateRef<any>;

  @Output() public setAsCv: EventEmitter<Attachment> = new EventEmitter();
  @Output() public setAsCoverLetter: EventEmitter<Attachment> = new EventEmitter<Attachment>();
  @Output() public deleteClick: EventEmitter<Attachment> = new EventEmitter();

  public additionalOptsDropdownOpen: boolean = false;
  public actions: AttachmentAction[] = [];
  public href: SafeUrl;
  public thumbnailUrl: string | null;
  public hasThumbnail: boolean;
  public isDocumentOrImage: boolean;
  public hasUploader: boolean;
  public avatarData: AttachmentAvatarData;
  public uploaderName: string | undefined;
  public isPrivate: boolean;
  public isExecutable: boolean;

  private attachmentActionsDef: Record<AttachmentActionId, AttachmentAction> = {
    [AttachmentActionId.SET_AS_CV]: {
      label: this.translations.setAsCv,
      icon: 'file-account',
      variant: 'primary-on-hover',
      handler: () => {
        this.setAsCv.emit(this.attachment);
        this.toggleAdditionalOptsDropdown();
      },
    },
    [AttachmentActionId.SET_AS_COVER_LETTER]: {
      label: this.translations.setAsCoverLetter,
      icon: 'file-document',
      variant: 'primary-on-hover',
      handler: () => {
        this.setAsCoverLetter.emit(this.attachment);
        this.toggleAdditionalOptsDropdown();
      },
    },
    [AttachmentActionId.DELETE]: {
      label: this.translations.delete,
      icon: 'delete',
      handler: () => this.delete(),
      variant: 'danger-on-hover',
    },
  };
  private fileExtensions = {
    cl: this.fileTypesService.byCategoryAsString[FileTypesCategoryEnum.CL],
    cv: this.fileTypesService.byCategoryAsString[FileTypesCategoryEnum.CV],
  };

  private thumbnailBroken: boolean = false;

  constructor(
    public sanitizer: DomSanitizer,
    private dialogService: DialogService,
    private viewContainerRef: ViewContainerRef,
    private windowRef: WindowRef,
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private fileTypesService: AcceptedFileTypesService,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    const actions: AttachmentAction[] = [];

    if (changes.attachment) {
      this.onAttachmentChange(this.attachment);
    }

    if (
      this.hasSetAsCvAction &&
      !this.fileTypesService.isInvalid(this.attachment.extension, this.fileExtensions.cv)
    ) {
      actions.push(this.attachmentActionsDef[AttachmentActionId.SET_AS_CV]);
    }

    if (
      this.hasSetAsCoverLetterAction &&
      !this.fileTypesService.isInvalid(this.attachment.extension, this.fileExtensions.cl)
    ) {
      actions.push(this.attachmentActionsDef[AttachmentActionId.SET_AS_COVER_LETTER]);
    }

    if (this.hasDeleteAction) {
      actions.push(this.attachmentActionsDef[AttachmentActionId.DELETE]);
    }

    this.actions = actions;
  }

  public onAnchorClick(event: MouseEvent) {
    const download = () => this.windowRef.nativeWindow.location.assign(this.attachment.fileUrl);

    if (this.isExecutable) {
      event.preventDefault();
      this.confirmExecutableDownload().subscribe(() => download());
    } else {
      download();
    }
  }

  public thumbnailLoadError(): void {
    this.thumbnailBroken = true;
    this.cdRef.detectChanges();
  }

  public trackByLabel(index: number, action: AttachmentAction): string {
    return action.label;
  }

  public toggleAdditionalOptsDropdown(
    value: boolean = !this.additionalOptsDropdownOpen,
    evt?: MouseEvent,
  ): void {
    if (evt) evt.preventDefault();

    this.additionalOptsDropdownOpen = value;
    this.cdRef.detectChanges();
  }

  public open(): void {
    if (this.isDocumentOrImage) {
      this.dialogService.custom(
        AttachmentPreviewDialogComponent,
        {
          width: DialogWidth.Large,
          closeTooltip: this.translations.previewDialog.closeDialogTooltip,
        },
        <AttachmentPreviewDialogData>{
          attachment: this.attachment,
          translations: this.translations.previewDialog,
        },
        this.viewContainerRef,
      );
    } else if (this.isExecutable) {
      this.confirmExecutableDownload().subscribe(() =>
        this.windowRef.nativeWindow.open(this.attachment.fileUrl),
      );
    } else {
      this.windowRef.nativeWindow.open(this.attachment.fileUrl);
    }
  }

  public delete(): void {
    this.deleteClick.emit(this.attachment);
  }

  private onAttachmentChange(attachment: Attachment) {
    this.thumbnailUrl = this.getThumbnailUrl(attachment);
    this.hasThumbnail = this.thumbnailUrl !== null && !this.thumbnailBroken;
    this.isDocumentOrImage = !!attachment?.pdfUrl || !!attachment?.imageUrl;
    this.hasUploader = !!attachment.admin || attachment.candidate || attachment.guest;
    this.avatarData = this.getAvatarData(attachment);
    this.uploaderName = this.getUploaderName(attachment);
    this.isPrivate = this.getIsPrivate(attachment);
    this.isExecutable = isExecutableFile(attachment.filename);
    this.setHref(attachment);
  }

  private setHref(attachment: Attachment): void {
    // eslint-disable-next-line ban/ban
    this.href = this.sanitizer.bypassSecurityTrustUrl(attachment.fileUrl);
  }

  private getThumbnailUrl(attachment: Attachment): string | null {
    return attachment.imageThumbnailUrl || attachment.pdfThumbnailUrl || null;
  }

  private getAvatarData(attachment: Attachment): AttachmentAvatarData {
    if (attachment.uploader === 'Admin' && attachment.admin) {
      const { initials, photoThumbUrl, hasAvatar, anonymizedAt } = attachment.admin;
      if (anonymizedAt) {
        return {
          photoThumbUrl,
          hasAvatar,
          initials,
          context: 'admin',
          type: 'icon',
          iconName: 'person',
        };
      }

      return {
        photoThumbUrl,
        hasAvatar,
        context: 'admin',
        initials,
        type: hasAvatar ? 'image' : 'initials',
      };
    }
    if (attachment.uploader === 'Candidate' && attachment.candidate) {
      const { initials, photoThumbUrl, hasAvatar } = attachment.candidate;

      return {
        photoThumbUrl,
        hasAvatar,
        context: 'candidate',
        initials,
        type: hasAvatar ? 'image' : 'initials',
      };
    }

    return {
      photoThumbUrl: attachment.guest?.photoThumbUrl,
      hasAvatar: true,
      context: 'other',
      initials: '',
      type: 'image',
    };
  }

  private getUploaderName(attachment: Attachment): string | undefined {
    if (attachment.uploader === 'Admin' && attachment.admin) {
      return attachment.admin.anonymizedAt
        ? this.i18n.translate('app.common.deleted_user')
        : `${attachment.admin.firstName} ${attachment.admin.lastName}`;
    }
    if (attachment.uploader === 'Candidate' && attachment.candidate) {
      return attachment.candidate.name;
    }
    if (attachment.uploader === 'Guest' && attachment.guest) {
      return attachment.guest.name;
    }
    return;
  }

  private getIsPrivate(attachment: Attachment): boolean {
    if (!attachment || !attachment.visibility) {
      return false;
    }

    return attachment.visibility.level !== VisibilityLevel.Public;
  }

  private confirmExecutableDownload() {
    const { title, message, agreeLabel, cancelLabel } = this.translations.executableWarningDialog;
    return this.dialogService
      .warning({
        width: DialogWidth.SmallXX,
        title: this.i18n.translate(title),
        message: this.i18n.translate(message),
        agreeLabel: this.i18n.translate(agreeLabel),
        cancelLabel: this.i18n.translate(cancelLabel),
      })
      .pipe(take(1), filterBoolean);
  }
}
